// The asset page (asset-detail-revamp-handover, owner 2026-09-24): a record that answers in three seconds what it is,
// where it is, whether it is all right and what is missing. Breadcrumb · header card · summary strip · Details (with a
// quiet completeness hint) beside the Asset tag · Photos — and a Lifecycle tab with the whole life of the asset
// (public.asset_lifecycle). The Overview does not repeat the history (owner, 2026-09-24: it duplicated the tab). Every action the page had is still here: Print label, Edit, the ⋮ menu
// (remove from service / put back / delete), the value dialog, zones, photos.
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router';
import { canManage, useSession } from '../auth/session';
import { AssetDialog, AssetStateChip } from '../components/asset/AssetDialog';
import { Card, CardHeader } from '../components/asset/Card';
import { CertificatesCard } from '../components/asset/CertificatesCard';
import { LogServiceDialog, ServiceCard } from '../components/asset/ServiceCard';
import { useServiceRules } from '../data/service';
import {
  ConditionCard,
  ConditionDialog,
  ServiceBanner,
  ServiceStateDialog,
} from '../components/asset/ConditionCard';
import { canSetServiceState } from '@fleetspoke/core/lifecycle';
import { LifecycleFeed } from '../components/asset/LifecycleFeed';
import { PhotoSlots } from '../components/asset/PhotoSlots';
import { TagCard } from '../components/asset/TagCard';
import { AssetRegisterMenu, RetireBanner } from '../components/AssetRegister';
import { money, ValueDialog, warrantyText } from '../components/AssetValue';
import { ZoneText } from '../components/ZoneText';
import { lifecycleKeys, useAssetTags, ELECTRONIC_TAG_TYPES } from '../data/lifecycle';
import { useAssetPhotos } from '../data/photos';
import { type AssetOverview, keys, useAsset, useHistory } from '../data/queries';
import { useLiveInvalidate } from '../data/realtime';
import { type AssetFinancials, useAssetFinancials } from '../data/register';
import { activeZones, type SiteZone, useSetAssetZone, useZones } from '../data/zones';
import {
  type CompletenessField,
  completeness,
  daysOnSite,
  isStale,
  STALE_AFTER_DAYS,
} from '../lib/assetDetail';
import { download, labelsPdf } from '../lib/labels';
import { appConfig, supabase } from '../lib/supabase';
import { calendarDate, dateTime, relativeTime, todayIn } from '../lib/time';
import { canActAt, pickableZones } from '../lib/zones';

/** One 50 × 25 mm roll label for this asset (company code from the asset's own account). */
async function printLabel(
  companyId: string,
  a: { asset_code: string; description: string | null },
) {
  const { data, error } = await supabase()
    .from('companies')
    .select('code, name')
    .eq('id', companyId)
    .single();
  if (error || !data) throw new Error(error?.message ?? 'company not found');
  const pdf = await labelsPdf(
    [
      {
        assetCode: a.asset_code,
        companyCode: data.code,
        companyName: data.name,
        description: a.description,
      },
    ],
    'roll_50x25',
    appConfig().publicUrl,
  );
  download(`label-${a.asset_code}.pdf`, pdf, 'application/pdf');
}

/** Move one asset to a zone of its custody site, or out of its zone (CR_20260914). */
function ZonePickDialog({
  asset,
  zones,
  onClose,
}: {
  asset: AssetOverview;
  zones: SiteZone[];
  onClose(): void;
}) {
  const { t } = useTranslation();
  const [zoneId, setZoneId] = useState(asset.zone_id ?? '');
  const setZone = useSetAssetZone();
  const skipped = setZone.data?.skipped.length;
  const save = () =>
    setZone.mutate(
      { siteId: asset.custody_site_id!, assetIds: [asset.asset_id], zoneId: zoneId || null },
      { onSuccess: (r) => (r.skipped.length ? undefined : onClose()) },
    );
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{`${t('assets.zone_change')} · ${asset.asset_code}`}</DialogTitle>
      <DialogContent dividers>
        <TextField
          select
          fullWidth
          label={t('assets.zone_pick', { site: asset.custody_site_name ?? '' })}
          value={zoneId}
          onChange={(e) => setZoneId(e.target.value)}
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
        >
          <MenuItem value="">{t('assets.zone_none')}</MenuItem>
          {pickableZones(zones, asset.zone_id).map((z) => (
            <MenuItem key={z.id} value={z.id} disabled={!z.is_active}>
              {z.name}
            </MenuItem>
          ))}
        </TextField>
        {setZone.error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {t('common.error_generic')} {(setZone.error as Error).message}
          </Alert>
        ) : null}
        {skipped ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('board.zone_skipped', { count: skipped })}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={setZone.isPending || zoneId === (asset.zone_id ?? '')}
          onClick={save}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * ← Assets / {category} / {code}. The arrow keeps its old behaviour: opened from inside the app it goes back there
 * (filters and tab kept); opened directly (a shared link, a scanned label) it goes to the Assets list.
 */
function AssetBreadcrumb({ a }: { a?: AssetOverview }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
  const backLabel = idx > 0 ? t('common.back') : t('nav.assets');
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
      <Tooltip title={backLabel}>
        {idx > 0 ? (
          <IconButton
            size="small"
            aria-label={backLabel}
            onClick={() => void navigate(-1)}
            sx={{ ml: -0.75 }}
            data-testid="asset-back"
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        ) : (
          <IconButton
            size="small"
            component={RouterLink}
            to="/assets"
            aria-label={backLabel}
            sx={{ ml: -0.75 }}
            data-testid="asset-back"
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
      </Tooltip>
      <Breadcrumbs aria-label={t('asset.breadcrumb')} sx={{ fontSize: 14 }}>
        <Link component={RouterLink} to="/assets" underline="hover" color="text.secondary">
          {t('nav.assets')}
        </Link>
        {a?.category_id ? (
          <Link
            component={RouterLink}
            to={`/assets?category=${a.category_id}`}
            underline="hover"
            color="text.secondary"
          >
            {a.category_name}
          </Link>
        ) : null}
        {a ? (
          <Typography color="text.primary" sx={{ fontSize: 14 }}>
            {a.asset_code}
          </Typography>
        ) : null}
      </Breadcrumbs>
    </Stack>
  );
}

/** One summary tile: muted label with icon, the value below; a whole-tile button when it opens something. */
function Tile({
  icon,
  label,
  value,
  tone,
  hint,
  onClick,
  testId,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  tone?: 'warning' | 'muted';
  hint?: string;
  onClick?: () => void;
  testId?: string;
}) {
  const body = (
    <Box sx={{ p: 1.75, width: '100%', textAlign: 'left' }}>
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{ color: 'text.secondary', mb: 0.5, '& svg': { fontSize: 16 } }}
      >
        {icon}
        <Typography variant="caption">{label}</Typography>
      </Stack>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: 17,
          color:
            tone === 'warning'
              ? 'warning.main'
              : tone === 'muted'
                ? 'text.secondary'
                : 'text.primary',
        }}
        noWrap
        data-testid={testId}
      >
        {value}
      </Typography>
    </Box>
  );
  const box = {
    bgcolor: 'action.hover',
    borderRadius: 2.5,
    minWidth: 0,
    display: 'flex',
  } as const;
  const inner = onClick ? (
    <ButtonBase onClick={onClick} sx={{ ...box, '&:hover': { bgcolor: 'action.selected' } }}>
      {body}
    </ButtonBase>
  ) : (
    <Box sx={box}>{body}</Box>
  );
  return hint ? (
    <Tooltip title={hint}>
      <Box sx={{ display: 'flex', minWidth: 0 }}>{inner}</Box>
    </Tooltip>
  ) : (
    inner
  );
}

/** Details row: 12 px muted label, 14 px value; an inline "+ Add" when it is empty and the viewer may fill it. */
function Field({
  id,
  label,
  value,
  onAdd,
  addLabel,
  children,
}: {
  id?: string;
  label: string;
  value?: ReactNode;
  onAdd?: () => void;
  addLabel?: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const empty = value == null || value === '';
  return (
    <Box id={id} tabIndex={id ? -1 : undefined} sx={{ minWidth: 0, outline: 'none' }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      {children ??
        (empty ? (
          onAdd ? (
            <Link
              component="button"
              variant="body2"
              underline="hover"
              onClick={onAdd}
              sx={{ fontWeight: 500 }}
            >
              {addLabel ?? t('asset.add')}
            </Link>
          ) : (
            <Typography variant="body2">—</Typography>
          )
        ) : (
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
            {value}
          </Typography>
        ))}
    </Box>
  );
}

const MISSING_TARGET: Record<CompletenessField, string> = {
  make_model: 'detail-make_model',
  serial: 'detail-serial',
  value: 'detail-value',
  photo: 'asset-photos',
  mounting: 'asset-tag',
};

/** The quiet "60% complete" hint (handover §3.4): muted text, a grey 64 × 3 bar, the gaps only in a tooltip. */
function Completeness({ pct, missing }: { pct: number; missing: CompletenessField[] }) {
  const { t } = useTranslation();
  const go = () => {
    const el = document.getElementById(MISSING_TARGET[missing[0]!]);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el?.focus({ preventScroll: true });
  };
  return (
    <Tooltip
      title={t('asset.missing', { list: missing.map((m) => t(`asset.field_${m}`)).join(', ') })}
    >
      <ButtonBase
        onClick={go}
        data-testid="completeness"
        aria-label={`${t('asset.complete', { pct })}. ${t('asset.missing', {
          list: missing.map((m) => t(`asset.field_${m}`)).join(', '),
        })}`}
        sx={{ gap: 1, borderRadius: 1, px: 0.5 }}
      >
        <Box sx={{ width: 64, height: 3, borderRadius: 2, bgcolor: 'divider', overflow: 'hidden' }}>
          <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: 'text.disabled' }} />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {t('asset.complete', { pct })}
        </Typography>
      </ButtonBase>
    </Tooltip>
  );
}

function PageSkeleton() {
  return (
    <Box data-testid="asset-skeleton">
      <Skeleton variant="rounded" height={104} sx={{ borderRadius: 3, mb: 2 }} />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 1.5,
          mb: 2,
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={68} sx={{ borderRadius: 2.5 }} />
        ))}
      </Box>
      <Box
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.6fr 1fr' }, gap: 2, mb: 2 }}
      >
        <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
        <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
      </Box>
      <Skeleton variant="rounded" height={180} sx={{ borderRadius: 3, mb: 2 }} />
      <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
    </Box>
  );
}

// the page's own frame: same width as every page, buttons and tabs in sentence case (MUI's default is capitals —
// changed here only, see the handover §8; the rest of the app keeps its look until that is decided globally)
function Frame({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: 1400,
        mx: 'auto',
        '& .MuiButton-root, & .MuiTab-root': { textTransform: 'none' },
      }}
    >
      {children}
    </Box>
  );
}

type Dialogs =
  | null
  | 'edit'
  | 'edit_make_model'
  | 'edit_serial'
  | 'value'
  | 'zone'
  | 'condition'
  | 'service'
  | 'log_service';

export function AssetDetailPage() {
  const { assetId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const { me } = useSession();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'lifecycle' ? 'lifecycle' : 'overview';
  const asset = useAsset(assetId);
  const a = asset.data;
  const manager = canManage(me);
  const history = useHistory(assetId);
  const photos = useAssetPhotos(assetId);
  const tags = useAssetTags(assetId);
  const fin = useAssetFinancials(assetId, manager);
  const zones = useZones(a?.custody_site_id);
  const [open, setOpen] = useState<Dialogs>(null);
  const [addingCert, setAddingCert] = useState(false);
  const serviceRules = useServiceRules();
  useLiveInvalidate(
    `asset-${assetId}`,
    ['asset_presence', 'asset_zone_moves'],
    [keys.asset(assetId), keys.history(assetId), ['zone-moves'], lifecycleKeys.asset(assetId)],
  );

  if (asset.isLoading) {
    return (
      <Frame>
        <AssetBreadcrumb />
        <PageSkeleton />
      </Frame>
    );
  }
  if (asset.error || !a) {
    const notFound = !asset.error || /\(or no\) rows|0 rows/i.test((asset.error as Error).message);
    return (
      <Frame>
        <AssetBreadcrumb />
        <Card data-testid={notFound ? 'asset-not-found' : 'asset-error'}>
          <Typography sx={{ mb: 1.5 }}>
            {notFound ? t('asset.not_found') : t('asset.load_error')}
          </Typography>
          {notFound ? (
            <Button component={RouterLink} to="/assets" variant="outlined">
              {t('asset.back_to_list')}
            </Button>
          ) : (
            <Button variant="outlined" onClick={() => void asset.refetch()}>
              {t('common.retry')}
            </Button>
          )}
        </Card>
      </Frame>
    );
  }

  const lang = i18n.language;
  const tz = me?.company?.timezone ?? 'Asia/Kuala_Lumpur';
  const active = a.asset_status === 'active';
  const canEdit = manager && active;
  const photoList = photos.data?.photos ?? [];
  const mounting = photos.data?.mounting ?? [];
  const tagList = tags.data ?? [];
  const hasDevice = tagList.some((x) =>
    (ELECTRONIC_TAG_TYPES as readonly string[]).includes(x.type),
  );
  const f: AssetFinancials | null = fin.data ?? null;
  const siteZones = activeZones(zones.data);
  const canPickZone = siteZones.length > 0 && active && canActAt(me, a.custody_site_id);
  const where =
    a.display_state === 'in_transit'
      ? (a.vehicle_ref ?? t('state.in_transit'))
      : (a.seen_site_name ??
        a.presence_site_name ??
        (a.custody_site_name ? t('assets.where_not_seen', { site: a.custody_site_name }) : '—'));
  const days =
    a.display_state === 'on_site' ? daysOnSite(history.data ?? [], a.presence_site_id, tz) : null;
  const stale = isStale(a.last_seen_at, a.has_electronic_tag);
  const done = completeness({
    makeModel: a.make_model,
    serial: a.serial,
    hasValue: f?.value != null,
    photos: photoList.length,
    mountingPhotos: mounting.length,
    hasElectronicTag: hasDevice,
  });
  const showCompleteness =
    canEdit && !fin.isLoading && !photos.isLoading && !tags.isLoading && done.pct < 100;
  const valueText = f?.value != null ? money(f.value, f.currency, lang) : null;
  const subtitle = [a.description, a.make_model].filter(Boolean).join(' · ');
  const firstPhoto = photoList[0]?.url ?? null;
  const print = () => void printLabel(a.company_id, a);
  const setTab = (v: string) => {
    const next = new URLSearchParams(params);
    if (v === 'overview') next.delete('tab');
    else next.set('tab', v);
    setParams(next, { replace: true });
  };

  const header = (
    <Card sx={{ mb: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          {firstPhoto ? (
            <ButtonBase
              component="a"
              href={firstPhoto}
              target="_blank"
              rel="noreferrer"
              aria-label={t('photos.photo_n', { n: 1 })}
              sx={{ width: 64, height: 64, borderRadius: 2, overflow: 'hidden', flex: 'none' }}
            >
              <Box
                component="img"
                src={firstPhoto}
                alt=""
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </ButtonBase>
          ) : (
            <Box
              aria-hidden
              sx={{
                width: 64,
                height: 64,
                borderRadius: 2,
                flex: 'none',
                bgcolor: 'action.hover',
                color: 'text.secondary',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Inventory2OutlinedIcon />
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography component="h1" sx={{ fontSize: 24, fontWeight: 500, lineHeight: 1.25 }}>
                {a.asset_code}
              </Typography>
              <AssetStateChip a={a} />
            </Stack>
            {subtitle ? (
              <Typography color="text.secondary" variant="body2" noWrap>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
          {/* anyone who can see the asset can reprint its label (a worn one, say) */}
          {!narrow ? (
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<PrintOutlinedIcon />}
              onClick={print}
            >
              {t('labels.print_one')}
            </Button>
          ) : null}
          {/* the one filled button on the page; a retired asset is read-only apart from Put back (A5) */}
          {canEdit ? (
            <Button
              variant="contained"
              startIcon={<EditOutlinedIcon />}
              onClick={() => setOpen('edit')}
            >
              {t('common.edit')}
            </Button>
          ) : null}
          <AssetRegisterMenu
            asset={a}
            extra={[
              ...(narrow ? [{ label: t('labels.print_one'), onClick: print }] : []),
              // CR_20260921 T1: the way in for a certificate on an asset whose category requires none
              // the dialog lives in the Overview's Certificates card: open it from the Lifecycle tab too
              ...(canEdit
                ? [
                    {
                      label: t('certs.add'),
                      onClick: () => {
                        setTab('overview');
                        setAddingCert(true);
                      },
                    },
                  ]
                : []),
              // CR_20260921 T2: anyone who can see it may report its condition; out of service per §4.5
              ...(active
                ? [{ label: t('condition.report'), onClick: () => setOpen('condition') }]
                : []),
              ...(active && canSetServiceState(me, a.custody_site_id)
                ? [
                    {
                      label: t(
                        a.service_state === 'out_of_service'
                          ? 'service.mark_in'
                          : 'service.mark_out',
                      ),
                      onClick: () => setOpen('service'),
                    },
                    // CR_20260921 T3: the same people log service and repairs (§4.5)
                    { label: t('svc.log_menu'), onClick: () => setOpen('log_service') },
                  ]
                : []),
            ]}
          />
        </Stack>
      </Stack>
    </Card>
  );

  const strip = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
        gap: { xs: 1, md: 1.5 },
        mb: 2,
      }}
      data-testid="summary-strip"
    >
      <Tile
        icon={<PlaceOutlinedIcon />}
        label={t('asset.location')}
        value={where}
        testId="tile-location"
      />
      <Tile
        icon={<CalendarMonthOutlinedIcon />}
        label={t('asset.days_on_site')}
        value={
          days == null ? '—' : days === 0 ? t('asset.today') : t('asset.days', { count: days })
        }
        testId="tile-days"
      />
      <Tile
        icon={<SensorsOutlinedIcon />}
        label={t('assets.last_seen')}
        value={
          <>
            {relativeTime(a.last_seen_at, lang) ?? t('assets.not_seen')}
            {stale ? (
              <WarningAmberIcon
                aria-label={t('asset.stale', { days: STALE_AFTER_DAYS })}
                sx={{ fontSize: 16, ml: 0.5, verticalAlign: '-2px' }}
              />
            ) : null}
          </>
        }
        tone={stale ? 'warning' : undefined}
        hint={
          a.last_seen_at
            ? [
                dateTime(a.last_seen_at, lang, tz),
                a.last_source ? t(`source.${a.last_source}`) : null,
                stale ? t('asset.stale', { days: STALE_AFTER_DAYS }) : null,
              ]
                .filter(Boolean)
                .join(' · ')
            : undefined
        }
        testId="tile-last-seen"
      />
      {/* money is managers' only: everyone else gets the zone in this place (A11) */}
      {manager ? (
        <Tile
          icon={<PaymentsOutlinedIcon />}
          label={t('value.value')}
          value={valueText ?? t('asset.not_set')}
          tone={valueText ? undefined : 'muted'}
          onClick={!valueText && active ? () => setOpen('value') : undefined}
          testId="tile-value"
        />
      ) : (
        <Tile
          icon={<GridViewOutlinedIcon />}
          label={t('assets.zone')}
          value={a.zone_name ?? '—'}
          testId="tile-zone"
        />
      )}
    </Box>
  );

  const details = (
    <Card data-testid="asset-details">
      <CardHeader
        title={t('asset.details')}
        aside={showCompleteness ? <Completeness pct={done.pct} missing={done.missing} /> : null}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
          columnGap: 3,
          rowGap: 2,
        }}
      >
        <Field label={t('assets.category')} value={a.category_name} />
        <Field
          id="detail-make_model"
          label={t('assets.make_model')}
          value={a.make_model}
          onAdd={canEdit ? () => setOpen('edit_make_model') : undefined}
        />
        <Field label={t('assets.custody_site')} value={a.custody_site_name} />
        <Field
          id="detail-serial"
          label={t('assets.serial')}
          value={a.serial}
          onAdd={canEdit ? () => setOpen('edit_serial') : undefined}
        />
        <Field label={t('assets.last_seen')}>
          <Typography variant="body2">
            {a.last_seen_at ? dateTime(a.last_seen_at, lang, tz) : t('assets.not_seen')}
            {a.last_seen_at && a.last_source ? (
              <Box component="span" sx={{ color: 'text.secondary' }}>
                {' · '}
                {t(`source.${a.last_source}`)}
              </Box>
            ) : null}
          </Typography>
        </Field>
        {a.custody_site_id && (siteZones.length > 0 || a.zone_id) ? (
          <Field label={t('assets.zone')}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box data-testid="asset-zone" sx={{ fontSize: 14 }}>
                <ZoneText a={a} />
              </Box>
              {canPickZone ? (
                <Link
                  component="button"
                  variant="body2"
                  underline="hover"
                  onClick={() => setOpen('zone')}
                >
                  {t('assets.zone_change')}
                </Link>
              ) : null}
            </Stack>
          </Field>
        ) : null}
        {manager ? (
          <>
            <Field
              id="detail-value"
              label={t('value.value')}
              value={
                valueText
                  ? [
                      valueText,
                      f?.purchased_at
                        ? t('asset.purchased_on', { date: calendarDate(f.purchased_at, lang) })
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : null
              }
              onAdd={active ? () => setOpen('value') : undefined}
              addLabel={t('asset.add_value')}
            />
            <Field
              label={t('value.supplier')}
              value={[f?.supplier_name ?? f?.supplier, f?.po_ref ? `PO ${f.po_ref}` : null]
                .filter(Boolean)
                .join(' · ')}
              onAdd={active ? () => setOpen('value') : undefined}
            />
            {f?.warranty_until ? (
              <Field
                label={t('value.warranty_until')}
                value={`${calendarDate(f.warranty_until, lang)} · ${warrantyText(f.warranty_until, todayIn(tz), lang, t)}`}
              />
            ) : null}
            {f?.value != null && active ? (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Link
                  component="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => setOpen('value')}
                >
                  {t('asset.edit_value')}
                </Link>
              </Box>
            ) : null}
          </>
        ) : null}
      </Box>
    </Card>
  );

  const overview = (
    <>
      {strip}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.6fr) minmax(0, 1fr)' },
          gap: { xs: 1.5, lg: 2 },
          mb: 2,
        }}
      >
        {details}
        <TagCard
          assetCode={a.asset_code}
          companyCode={me?.company?.code}
          tags={tagList}
          mounting={mounting}
          loading={tags.isLoading || photos.isLoading}
        />
      </Box>
      <Box>
        <PhotoSlots
          assetId={a.asset_id}
          companyId={a.company_id}
          photos={photoList}
          loading={photos.isLoading}
        />
      </Box>
      {/* CR_20260921_Asset_Lifecycle §7.1: current state only — the history is the Lifecycle tab */}
      <Stack
        spacing={{ xs: 1.5, lg: 2 }}
        sx={{ mt: { xs: 1.5, lg: 2 }, '&:empty': { display: 'none' } }}
      >
        <ConditionCard
          asset={a}
          onReport={active ? () => setOpen('condition') : undefined}
          onHistory={() => setTab('lifecycle')}
        />
        <ServiceCard
          assetId={a.asset_id}
          companyId={a.company_id}
          manager={manager}
          onLog={
            active && canSetServiceState(me, a.custody_site_id)
              ? () => setOpen('log_service')
              : undefined
          }
        />
        <CertificatesCard
          assetId={a.asset_id}
          companyId={a.company_id}
          canEdit={canEdit}
          adding={addingCert}
          onAddingDone={() => setAddingCert(false)}
        />
      </Stack>
    </>
  );

  return (
    <Frame>
      <AssetBreadcrumb a={a} />
      {header}
      <RetireBanner asset={a} />
      <ServiceBanner asset={a} />
      <Tabs
        value={tab}
        onChange={(_, v: string) => setTab(v)}
        sx={{ mb: 2, minHeight: 40, '& .MuiTab-root': { minHeight: 40 } }}
      >
        <Tab value="overview" label={t('asset.tab_overview')} />
        <Tab value="lifecycle" label={t('asset.tab_lifecycle')} data-testid="tab-lifecycle" />
      </Tabs>
      {tab === 'overview' ? (
        overview
      ) : (
        <Card>
          <CardHeader title={t('asset.tab_lifecycle')} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('lifecycle.intro')}
          </Typography>
          <LifecycleFeed assetId={a.asset_id} pageSize={20} more />
        </Card>
      )}
      {open === 'edit' || open === 'edit_make_model' || open === 'edit_serial' ? (
        <AssetDialog
          open
          onClose={() => setOpen(null)}
          initial={a}
          focus={
            open === 'edit_make_model'
              ? 'make_model'
              : open === 'edit_serial'
                ? 'serial'
                : undefined
          }
        />
      ) : null}
      {open === 'value' ? (
        <ValueDialog asset={a} current={f} onClose={() => setOpen(null)} />
      ) : null}
      {open === 'condition' ? <ConditionDialog asset={a} onClose={() => setOpen(null)} /> : null}
      {open === 'service' ? <ServiceStateDialog asset={a} onClose={() => setOpen(null)} /> : null}
      {open === 'log_service' ? (
        <LogServiceDialog
          assetId={a.asset_id}
          companyId={a.company_id}
          manager={manager}
          tasks={(serviceRules.data ?? [])
            .filter((r) => r.category_id === a.category_id)
            .map((r) => r.task)}
          today={todayIn(tz)}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {open === 'zone' ? (
        <ZonePickDialog asset={a} zones={zones.data ?? []} onClose={() => setOpen(null)} />
      ) : null}
    </Frame>
  );
}
